import { z } from 'zod'

import { baseRegistrySchema } from '@/registry'
import {
  docVersionSchema,
  nanoidSchema,
  registryMetaSchema,
  timestampSchema,
  unixTimestamp,
} from '@/utility'

import { hotKeyConfigSchema } from './hotkeys'

/**
 * Workspace JSON
 *
 * This is the workspace representation that is stored
 * in GCS and returned through hosting proxy
 *
 * It can be consumed by any Scalar Client to populate
 * the workspace
 */
export const workspaceJsonSchema = z
  .object({
    /** Customize hotkeys */
    hotKeyConfig: hotKeyConfigSchema,
    /** Workspace level proxy for all requests to be sent through */
    proxyUrl: z.string().optional(),
    /** Workspace level theme */
    theme: z.string().optional(),
    /** Ordered list of documents to add to the workspace */
    documents: z.record(
      z.string(),
      z.looseObject({
        title: z.string().optional(),
        $ref: z.string().optional(),
      }),
    ),
    /**
     * Workspace level spec extensions.
     *
     * This must be an OAS Compliant JSON object that extends a spec with additional
     * properties such as extra servers, cookies, auth, and environments
     */
    extensions: z.looseObject({}),
  })
  .meta({ id: 'workspace-json' })

export type WorkspaceJson = z.infer<typeof workspaceJsonSchema>

/**
 * Versioned Workspace
 *
 * Versions allow users to modify their workspace whilst
 * keeping a log of any changes made
 */
export const workspaceVersionSchema = z
  .object({
    version: docVersionSchema,
    /**
     * We also keep a list of any managed docs used
     * in the workspace version, in case we need to
     * trigger any platform actions or update the
     * workspace JSON
     */
    managedDocs: registryMetaSchema.array(),
    createdAt: timestampSchema
      .default(unixTimestamp)
      .meta({ default: 'unixTimestamp()' }),
    updatedAt: timestampSchema
      .default(unixTimestamp)
      .meta({ default: 'unixTimestamp()' }),
  })
  .meta({ id: 'workspace-version' })

export type WorkspaceVersion = z.infer<typeof workspaceVersionSchema>

/**
 * Workspace Entity
 *
 * Workspaces are a collection of Registry documents. These
 * are used for Scalar Client sync, and also in hosting proxy
 * for registry collection preview.
 */
export const workspaceSchema = baseRegistrySchema
  .extend({
    uid: nanoidSchema,
    /** The current version served to consumers by default */
    version: docVersionSchema,
    versions: workspaceVersionSchema.array(),
  })
  .meta({ id: 'workspace' })

export type Workspace = z.infer<typeof workspaceSchema>
